<script setup lang="ts">
import { ref } from "vue";
import { UiChip, UiDialog, UiIconButton, UiInfoBox, UiLinkButton, UiSwitch, UiText, UiTooltip } from "@shopexperts/shared";
import type { PlanBillingOption } from "@shopexperts/legacy-api";
import {
  formatBillingInterval,
  formatOptionAmount,
} from "@/app/composables/expert-billing/billingOptionForm";
import { formatPrice } from "@/app/composables/expert-plans/expertPlanForm";

defineProps<{
  options: PlanBillingOption[];
  isToggling?: boolean;
}>();

const emit = defineEmits<{
  (e: "edit", option: PlanBillingOption): void;
  (e: "delete", option: PlanBillingOption): void;
  (e: "toggle", option: PlanBillingOption): void;
}>();

const priceHistoryOpen = ref(false);
const priceHistoryOption = ref<PlanBillingOption | null>(null);

function openPriceHistory(option: PlanBillingOption) {
  priceHistoryOption.value = option;
  priceHistoryOpen.value = true;
}

function closePriceHistory() {
  priceHistoryOpen.value = false;
  priceHistoryOption.value = null;
}

interface SyncChip {
  text: string;
  variant: "warn" | "success" | "neutral";
  tooltip: string;
  /** The chip set has no danger variant; failed states override neutral to red. */
  bgOverride?: string;
  textOverride?: string;
}

function syncChips(option: PlanBillingOption): SyncChip[] {
  const syncs = option.stripe_syncs ?? [];
  if (syncs.length === 0) {
    return [{
      text: "Ready to activate",
      variant: "neutral",
      tooltip: "Turn on Sellable when ready. Stripe will synchronize this option automatically.",
    }];
  }
  return syncs.map((sync) => {
    const state = sync.status === "synced"
      ? "synced"
      : sync.status === "failed"
        ? "needs attention"
        : option.is_active
          ? "syncing"
          : "ready";
    return {
      text: `${sync.mode}: ${state}`,
      variant: sync.status === "synced" ? "success" : sync.status === "failed" ? "neutral" : "warn",
      tooltip: sync.status === "failed"
        ? `Stripe ${sync.mode} sync failed. Correct the option, then turn on Sellable to retry automatically.`
        : `Stripe ${sync.mode} mode is selected by the server's configured key (${sync.mode === "live" ? "production" : "sandbox"}). Activation and active edits synchronize automatically.`,
      bgOverride: sync.status === "failed" ? "bg-red-100" : undefined,
      textOverride: sync.status === "failed" ? "text-red-800" : undefined,
    } as SyncChip;
  });
}

function sellableTooltip(option: PlanBillingOption): string {
  if (option.is_active) {
    return "Sellable through payment invitations. Turn off to stop new sales; Stripe history is retained.";
  }
  if ((option.stripe_syncs ?? []).some((sync) => sync.status === "failed")) {
    return "Turn on to retry activation. Stripe synchronizes automatically before the option becomes sellable.";
  }
  return "Turn on to synchronize with Stripe and make this option sellable.";
}

function failedSyncs(option: PlanBillingOption) {
  return (option.stripe_syncs ?? []).filter((sync) => sync.status === "failed" && sync.last_error);
}

function formatHistoryDate(value: string | null | undefined): string {
  if (!value) return "Not recorded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not recorded";
  return date.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
}
</script>

<template>
  <div class="overflow-x-auto">
    <table class="w-full min-w-[600px] border-collapse">
      <thead class="bg-se-gray-50">
        <tr class="border-b border-se-gray-200">
          <th scope="col" class="px-3 py-2.5 text-left">
            <UiText as="span" variant="caption" class="text-se-gray-600" text="Option" />
          </th>
          <th scope="col" class="whitespace-nowrap px-3 py-2.5 text-right">
            <UiText as="span" variant="caption" class="text-se-gray-600" text="Price" />
          </th>
          <th scope="col" class="px-3 py-2.5 text-left">
            <UiText as="span" variant="caption" class="text-se-gray-600" text="Stripe status" />
          </th>
          <th scope="col" class="whitespace-nowrap px-3 py-2.5 text-left">
            <UiText as="span" variant="caption" class="text-se-gray-600" text="Sellable" />
          </th>
          <th scope="col" class="whitespace-nowrap px-3 py-2.5 text-right">
            <UiText as="span" variant="caption" class="text-se-gray-600" text="Actions" />
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-for="option in options" :key="option.id">
          <tr class="border-b border-se-gray-200 align-middle">
            <td class="px-3 py-4">
              <UiText as="p" variant="p" class="font-semibold text-se-dark" :text="option.label" />
              <UiText
                as="p"
                variant="caption-normal"
                class="text-se-gray-500"
                :text="formatBillingInterval(option)"
              />
            </td>
            <td class="whitespace-nowrap px-3 py-4 text-right">
              <UiTooltip :content="`View Stripe price history for ${option.label}`">
                <UiLinkButton
                  variant="ghost"
                  text-variant="p"
                  hover-decoration="underline"
                  class="!mx-0 !px-0 tabular-nums"
                  :label="formatOptionAmount(option)"
                  :aria-label="`View Stripe price history for ${option.label}`"
                  @click="openPriceHistory(option)"
                />
              </UiTooltip>
            </td>
            <td class="px-3 py-4">
              <div class="flex flex-col items-start gap-1.5">
                <UiChip
                  v-for="chip in syncChips(option)"
                  :key="chip.text"
                  :text="chip.text"
                  :variant="chip.variant"
                  size="xs"
                  :tooltip="chip.tooltip"
                  :bg-color-class="chip.bgOverride"
                  :text-color-class="chip.textOverride"
                />
              </div>
            </td>
            <td class="whitespace-nowrap px-3 py-4">
              <UiTooltip :content="sellableTooltip(option)">
                <span class="inline-flex">
                  <UiSwitch
                    size="sm"
                    :name="`option-${option.id}-active`"
                    :model-value="option.is_active"
                    :disabled="isToggling"
                    @update:model-value="emit('toggle', option)"
                  />
                </span>
              </UiTooltip>
            </td>
            <td class="whitespace-nowrap px-3 py-4 text-right">
              <span class="inline-flex items-center gap-2">
                <UiTooltip content="Edit option">
                  <UiIconButton
                    icon="pencil"
                    variant="outline"
                    class="!rounded-md !p-2.5"
                    :aria-label="`Edit ${option.label}`"
                    @click="emit('edit', option)"
                  />
                </UiTooltip>
                <UiTooltip content="Delete option">
                  <UiIconButton
                    icon="delete"
                    variant="danger"
                    class="!rounded-md !p-2.5"
                    :aria-label="`Delete ${option.label}`"
                    @click="emit('delete', option)"
                  />
                </UiTooltip>
              </span>
            </td>
          </tr>

          <tr v-if="failedSyncs(option).length" class="border-b border-se-gray-200">
            <td colspan="5" class="px-3 pb-3">
              <div class="flex flex-col gap-2">
                <UiInfoBox
                  v-for="sync in failedSyncs(option)"
                  :key="`err-${option.id}-${sync.mode}`"
                  variant="error"
                  :title="`${sync.mode} sync failed`"
                >
                  <UiText as="p" variant="caption-normal" :text="sync.last_error ?? ''" />
                </UiInfoBox>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>

  <UiDialog
    v-model="priceHistoryOpen"
    :title="priceHistoryOption ? `${priceHistoryOption.label} price history` : 'Price history'"
    size="xl"
    @close="closePriceHistory"
  >
    <div v-if="priceHistoryOption" class="flex flex-col gap-4">
      <UiText
        as="p"
        variant="p-small"
        class="text-se-gray-600"
        text="Stripe prices are immutable. Pricing changes create a new current version while existing subscriptions may remain on an older one."
      />

      <div v-if="(priceHistoryOption.stripe_prices ?? []).length > 0" class="overflow-x-auto">
        <table class="w-full min-w-[680px] border-collapse">
          <thead class="bg-se-gray-50">
            <tr class="border-b border-se-gray-200">
              <th scope="col" class="whitespace-nowrap px-3 py-2.5 text-left">
                <UiText as="span" variant="caption" class="text-se-gray-600" text="Version" />
              </th>
              <th scope="col" class="whitespace-nowrap px-3 py-2.5 text-left">
                <UiText as="span" variant="caption" class="text-se-gray-600" text="Terms" />
              </th>
              <th scope="col" class="px-3 py-2.5 text-left">
                <UiText as="span" variant="caption" class="text-se-gray-600" text="Stripe references" />
              </th>
              <th scope="col" class="whitespace-nowrap px-3 py-2.5 text-left">
                <UiText as="span" variant="caption" class="text-se-gray-600" text="Lifecycle" />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="price in priceHistoryOption.stripe_prices ?? []"
              :key="price.id"
              class="border-b border-se-gray-200 align-top last:border-b-0"
            >
              <td class="px-3 py-3">
                <div class="flex flex-col items-start gap-1.5">
                  <UiChip
                    :text="price.is_current ? 'Current' : 'Retired'"
                    :variant="price.is_current ? 'success' : 'neutral'"
                    size="xs"
                  />
                  <UiChip
                    :text="price.mode === 'live' ? 'Live' : 'Test'"
                    variant="plain"
                    size="xs"
                    :tooltip="price.mode === 'live' ? 'Production Stripe environment' : 'Sandbox Stripe environment'"
                  />
                </div>
              </td>
              <td class="whitespace-nowrap px-3 py-3">
                <div class="flex flex-col gap-0.5">
                  <UiText
                    as="span"
                    variant="p-small"
                    class="font-semibold tabular-nums text-se-dark"
                    :text="formatPrice(price.amount_cents, price.currency)"
                  />
                  <UiText
                    as="span"
                    variant="caption-normal"
                    class="text-se-gray-500"
                    :text="formatBillingInterval(price)"
                  />
                </div>
              </td>
              <td class="px-3 py-3">
                <div class="flex flex-col gap-1.5">
                  <div class="flex min-w-0 items-baseline gap-1.5">
                    <UiText as="span" variant="caption-normal" class="shrink-0 text-se-gray-500" text="Price" />
                    <UiText
                      as="span"
                      variant="caption-normal"
                      class="min-w-0 break-all font-mono text-se-gray-700"
                      :text="price.stripe_price_id"
                    />
                  </div>
                  <div class="flex min-w-0 items-baseline gap-1.5">
                    <UiText as="span" variant="caption-normal" class="shrink-0 text-se-gray-500" text="Product" />
                    <UiText
                      as="span"
                      variant="caption-normal"
                      class="min-w-0 break-all font-mono text-se-gray-700"
                      :text="price.stripe_product_id"
                    />
                  </div>
                </div>
              </td>
              <td class="whitespace-nowrap px-3 py-3">
                <div v-if="price.is_current" class="flex flex-col gap-0.5">
                  <UiText as="span" variant="p-small" class="font-semibold text-se-dark" text="Current version" />
                  <UiText
                    as="span"
                    variant="caption-normal"
                    class="text-se-gray-500"
                    :text="price.deactivated_in_stripe_at
                      ? `Deactivated in Stripe ${formatHistoryDate(price.deactivated_in_stripe_at)}`
                      : 'Available in Stripe'"
                  />
                </div>
                <div v-else class="flex flex-col gap-1.5">
                  <div class="flex flex-col gap-0.5">
                    <UiText as="span" variant="caption-normal" class="text-se-gray-500" text="Retired" />
                    <UiText
                      as="span"
                      variant="p-small"
                      class="font-semibold text-se-dark"
                      :text="formatHistoryDate(price.retired_at)"
                    />
                  </div>
                  <div class="flex flex-col gap-0.5">
                    <UiText as="span" variant="caption-normal" class="text-se-gray-500" text="Deactivated in Stripe" />
                    <UiText
                      as="span"
                      variant="caption-normal"
                      class="text-se-gray-700"
                      :text="formatHistoryDate(price.deactivated_in_stripe_at)"
                    />
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <UiInfoBox
        v-else
        variant="info"
        title="No Stripe price history yet"
      >
        <UiText
          as="p"
          variant="caption-normal"
          text="A price version will appear here after this option synchronizes with Stripe."
        />
      </UiInfoBox>
    </div>
  </UiDialog>
</template>
